import React, { useState, useEffect } from 'react';
import api from '../services/api';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Eye,
  Heart,
  MessageCircle,
  Newspaper,
  RefreshCw,
  Share2,
  Bookmark,
  CreditCard
} from 'lucide-react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import './Analytics.css';

function Analytics() {
  const [stats, setStats] = useState({
    users: { total: 0, growth: 0 },
    views: { total: 0, growth: 0 },
    likes: { total: 0, growth: 0 },
    comments: { total: 0, growth: 0 },
    posts: { total: 0 },
    shares: { total: 0 },
    bookmarks: { total: 0 },
    subscriptions: { total: 0 }
  });
  const [chartData, setChartData] = useState({
    views: [],
    users: [],
    likes: [],
    comments: [],
    shares: [],
    bookmarks: []
  });
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('week');
  const [activeChart, setActiveChart] = useState('views');

  useEffect(() => {
    loadAnalytics();
  }, [period]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      const [totalsRes, viewsRes, usersRes, likesRes, commentsRes, sharesRes, bookmarksRes] = await Promise.all([
        api.get('/api/admin/analytics/totals'),
        api.get('/api/admin/analytics/chart', { params: { period, type: 'views' } }),
        api.get('/api/admin/analytics/chart', { params: { period, type: 'users' } }),
        api.get('/api/admin/analytics/chart', { params: { period, type: 'likes' } }),
        api.get('/api/admin/analytics/chart', { params: { period, type: 'comments' } }),
        api.get('/api/admin/analytics/chart', { params: { period, type: 'shares' } }),
        api.get('/api/admin/analytics/chart', { params: { period, type: 'bookmarks' } })
      ]);

      if (totalsRes.data.success) {
        const data = totalsRes.data.data || {};
        setStats({
          users: { total: data.users || 0, growth: data.usersGrowth || 0 },
          views: { total: data.views || 0, growth: data.viewsGrowth || 0 },
          likes: { total: data.likes || 0, growth: data.likesGrowth || 0 },
          comments: { total: data.comments || 0, growth: data.commentsGrowth || 0 },
          posts: { total: data.posts || 0 },
          shares: { total: data.shares || 0 },
          bookmarks: { total: data.bookmarks || 0 },
          subscriptions: { total: data.subscriptions || 0 }
        });
      }

      setChartData({
        views: viewsRes.data.success ? (viewsRes.data.data || []) : [],
        users: usersRes.data.success ? (usersRes.data.data || []) : [],
        likes: likesRes.data.success ? (likesRes.data.data || []) : [],
        comments: commentsRes.data.success ? (commentsRes.data.data || []) : [],
        shares: sharesRes.data.success ? (sharesRes.data.data || []) : [],
        bookmarks: bookmarksRes.data.success ? (bookmarksRes.data.data || []) : []
      });
    } catch (error) {
      console.error('Ошибка загрузки аналитики:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ icon: Icon, title, value, growth, color, onClick, active }) => (
    <div 
      className={`analytics-stat-card ${active ? 'active' : ''} ${onClick ? 'clickable' : ''}`}
      onClick={onClick}
    >
      <div className={`stat-icon ${color}`}>
        <Icon size={24} />
      </div>
      <div className="stat-content">
        <span className="stat-title">{title}</span>
        <div className="stat-value-row">
          <span className="stat-value">{value.toLocaleString()}</span>
          {growth !== undefined && growth !== 0 && (
            <span className={`stat-growth ${growth > 0 ? 'positive' : 'negative'}`}>
              {growth > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              {Math.abs(growth)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const chartConfigs = {
    views: { title: 'Просмотры', color: '#f59e0b', icon: Eye },
    users: { title: 'Новые пользователи', color: '#10b981', icon: Users },
    likes: { title: 'Лайки', color: '#ef4444', icon: Heart },
    comments: { title: 'Комментарии', color: '#06b6d4', icon: MessageCircle },
    shares: { title: 'Поделились', color: '#8b5cf6', icon: Share2 },
    bookmarks: { title: 'Закладки', color: '#f97316', icon: Bookmark }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <span>Загрузка аналитики...</span>
      </div>
    );
  }

  const currentChartConfig = chartConfigs[activeChart];
  const ChartIcon = currentChartConfig.icon;
  const currentData = chartData[activeChart] || [];

  return (
    <div className="analytics-page fade-in">
      <div className="page-header">
        <h1 className="page-title">Аналитика</h1>
        <div className="header-actions">
          <select 
            className="input select period-select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="week">За 7 дней</option>
            <option value="month">За 30 дней</option>
            <option value="year">За год</option>
          </select>
          <button className="btn btn-secondary" onClick={loadAnalytics}>
            <RefreshCw size={18} />
            Обновить
          </button>
        </div>
      </div>

      {/* Stats Cards - Row 1 */}
      <div className="analytics-stats">
        <StatCard 
          icon={Users} 
          title="Пользователи" 
          value={stats.users.total} 
          growth={stats.users.growth}
          color="blue"
          onClick={() => setActiveChart('users')}
          active={activeChart === 'users'}
        />
        <StatCard 
          icon={Eye} 
          title="Просмотры" 
          value={stats.views.total} 
          growth={stats.views.growth}
          color="orange"
          onClick={() => setActiveChart('views')}
          active={activeChart === 'views'}
        />
        <StatCard 
          icon={Heart} 
          title="Лайки" 
          value={stats.likes.total} 
          growth={stats.likes.growth}
          color="red"
          onClick={() => setActiveChart('likes')}
          active={activeChart === 'likes'}
        />
        <StatCard 
          icon={MessageCircle} 
          title="Комментарии" 
          value={stats.comments.total} 
          growth={stats.comments.growth}
          color="cyan"
          onClick={() => setActiveChart('comments')}
          active={activeChart === 'comments'}
        />
      </div>

      {/* Stats Cards - Row 2 */}
      <div className="analytics-stats">
        <StatCard 
          icon={Newspaper} 
          title="Новости" 
          value={stats.posts.total}
          color="purple"
        />
        <StatCard 
          icon={Share2} 
          title="Поделились" 
          value={stats.shares.total}
          color="indigo"
          onClick={() => setActiveChart('shares')}
          active={activeChart === 'shares'}
        />
        <StatCard 
          icon={Bookmark} 
          title="Закладки" 
          value={stats.bookmarks.total}
          color="amber"
          onClick={() => setActiveChart('bookmarks')}
          active={activeChart === 'bookmarks'}
        />
        <StatCard 
          icon={CreditCard} 
          title="Подписки" 
          value={stats.subscriptions.total}
          color="green"
        />
      </div>

      {/* Main Chart */}
      <div className="analytics-charts">
        <div className="card chart-card full-width">
          <div className="card-header">
            <h3 className="card-title">
              <ChartIcon size={20} />
              {currentChartConfig.title} за период
            </h3>
            <div className="chart-tabs">
              {Object.entries(chartConfigs).map(([key, config]) => (
                <button
                  key={key}
                  className={`chart-tab ${activeChart === key ? 'active' : ''}`}
                  onClick={() => setActiveChart(key)}
                >
                  <config.icon size={16} />
                  {config.title}
                </button>
              ))}
            </div>
          </div>
          <div className="chart-container">
            {currentData.length > 0 ? (
              <ResponsiveContainer width="100%" height={350}>
                <AreaChart data={currentData}>
                  <defs>
                    <linearGradient id={`gradient-${activeChart}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={currentChartConfig.color} stopOpacity={0.3}/>
                      <stop offset="95%" stopColor={currentChartConfig.color} stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                  <XAxis 
                    dataKey="label" 
                    stroke="rgba(255,255,255,0.5)"
                    style={{ fontSize: '12px' }}
                  />
                  <YAxis 
                    stroke="rgba(255,255,255,0.5)"
                    style={{ fontSize: '12px' }}
                  />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'rgba(20, 20, 30, 0.95)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      borderRadius: '8px',
                      color: '#fff'
                    }}
                    labelStyle={{ color: '#fff' }}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="value" 
                    stroke={currentChartConfig.color}
                    strokeWidth={2}
                    fill={`url(#gradient-${activeChart})`}
                    animationDuration={500}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ 
                height: 350, 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: 'var(--text-muted)'
              }}>
                Нет данных для отображения
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Analytics;
